import { FilterOutlined } from "@ant-design/icons"
import { Badge, Button, Flex, Popover, Select, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { DateRangeFilter } from "../../../components/Filters/DateRangeFilter.tsx"
import {
  INVOICE_ORIGINS,
  type InvoicesListFilters
} from "../types/invoices.types.ts"

const { Text } = Typography

interface InvoicesMoreFiltersProps {
  filters: InvoicesListFilters
  disabled?: boolean
  onFilterChange: (patch: Partial<InvoicesListFilters>) => void
}

/**
 * Origin and the issued-date range — filters used far less than status,
 * customer or due date, so they move behind one popover instead of widening
 * the toolbar. The page's single "Clear filters" button already resets these
 * along with everything else, so this panel carries no clear action of its own.
 */
export function InvoicesMoreFilters({
  filters,
  disabled,
  onFilterChange
}: InvoicesMoreFiltersProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const originOptions = [
    { value: INVOICE_ORIGINS.GENERATED, label: t("invoices.origin.generated") },
    { value: INVOICE_ORIGINS.EXTERNAL, label: t("invoices.origin.external") }
  ]

  const activeCount = [
    filters.origin,
    filters.date_issued_from || filters.date_issued_to
  ].filter(Boolean).length

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      content={
        <Flex vertical gap="middle" className="w-72">
          <Flex vertical gap={4}>
            <Text type="secondary" className="text-xs font-medium">
              {t("invoices.columns.origin")}
            </Text>
            <Select
              allowClear
              aria-label={t("invoices.columns.origin")}
              className="w-full"
              placeholder={t("invoices.columns.origin")}
              options={originOptions}
              value={filters.origin ?? undefined}
              disabled={disabled}
              onChange={(value) => onFilterChange({ origin: value ?? null })}
            />
          </Flex>
          <Flex vertical gap={4}>
            <Text type="secondary" className="text-xs font-medium">
              {t("invoices.filters.issuedDate")}
            </Text>
            <DateRangeFilter<InvoicesListFilters>
              after={filters.date_issued_from}
              before={filters.date_issued_to}
              afterKey="date_issued_from"
              beforeKey="date_issued_to"
              disabled={disabled}
              className="w-full"
              placeholder={t("invoices.filters.issuedDate")}
              onChange={onFilterChange}
            />
          </Flex>
        </Flex>
      }
    >
      <Badge count={activeCount} size="small">
        <Button icon={<FilterOutlined />} disabled={disabled}>
          {t("invoices.filters.moreFilters")}
        </Button>
      </Badge>
    </Popover>
  )
}
